import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../shared/api.js';
import { Card } from '../../../layout/Card.jsx';
import { Icon } from '../../../layout/Icon.jsx';
import { formatIQD, formatNumber } from '../../../shared/format.js';
import { useAuth } from '../../../shared/useAuth.js';
import { useCheckout } from '../hooks/useCheckout.js';
import { useShiftStatus } from '../../../shared/useShift.js';
import { ShiftDrawerNote } from '../../../shared/ShiftDrawerNote.jsx';

const METHODS = [
  { key: 'cash', label: 'نقدي', emoji: '💵' },
  { key: 'card_balance', label: 'رصيد الكارت', emoji: '🎫' },
  { key: 'card', label: 'بطاقة', emoji: '💳' },
  { key: 'credit', label: 'آجل', emoji: '📒' }
];

const QUICK_AMOUNTS = [5000, 10000, 25000, 50000];

const CASHBOX_TYPE_LABELS = {
  reception: 'استقبال',
  cafeteria: 'كافتيريا',
  toys: 'ألعاب',
  accounting: 'محاسبة',
  main: 'رئيسية',
  expense: 'مصاريف',
  other: 'أخرى'
};

export function PaymentBox({
  totals,
  cardBalance = 0,
  hasCard = false,
  onSettle,
  onToggleOverride,
  settleLoading
}) {
  const payment = useCheckout((s) => s.payment);
  const setPayment = useCheckout((s) => s.setPayment);
  const override = useCheckout((s) => s.override);
  const setOverride = useCheckout((s) => s.setOverride);
  const hasPermission = useAuth((s) => s.hasPermission);

  const canOverride = hasPermission('checkout.override') || hasPermission('pos.discount');
  const me = useAuth((s) => s.user);
  const assignedCashboxId = me?.assignedCashboxId?._id || me?.assignedCashboxId || null;

  // نجلب كل القاصات النشطة (بدون فلترة نوع) — قاصة الكاشير المعيّنة قد تكون
  // reception/main/إلخ، وفلترة type=accounting كانت تخفيها وتترك القائمة فاضية.
  // الـ backend لا يشترط نوعاً معيناً عند التسوية.
  const cashboxQuery = useQuery({
    queryKey: ['cashboxes', 'active'],
    queryFn: async () => {
      const { data } = await api.get('/cashbox', { params: { active: 'true' } });
      return data?.items || data?.cashboxes || data?.data || data || [];
    }
  });
  const cashboxes = cashboxQuery.data || [];

  // أثناء الشفت: نقد التسوية يدخل درج الشفت حصراً (الخادم يفرضه أيضاً)
  const { shift, cashboxId: shiftBox } = useShiftStatus();
  useEffect(() => {
    if (shiftBox && payment.cashboxId !== shiftBox) setPayment({ cashboxId: shiftBox });
  }, [shiftBox]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (shiftBox || payment.cashboxId || !cashboxes.length) return;
    // أولوية: قاصة المستخدم المعيّنة → القاصة الرئيسية isMain → أول قاصة.
    const preferred = assignedCashboxId
      ? cashboxes.find((cb) => String(cb._id || cb.id) === String(assignedCashboxId))
      : null;
    const mainCb = !preferred ? cashboxes.find((cb) => cb.isMain) : null;
    const pick = preferred || mainCb || cashboxes[0];
    setPayment({ cashboxId: pick._id || pick.id });
  }, [cashboxes, assignedCashboxId]); // eslint-disable-line react-hooks/exhaustive-deps

  const due = Math.max(0, Number(totals?.due || 0));
  const paidNow = Number(payment.paidAmount) || 0;
  const change = Math.max(0, paidNow - due);
  const remaining = Math.max(0, due - paidNow);

  const isCredit = payment.method === 'credit';
  const isCardBalance = payment.method === 'card_balance';
  // القاصة مطلوبة للنقد فقط — الدفع من رصيد الكارت لا يمسّ أي قاصة
  // (المال دخل القاصة أصلاً وقت تعبئة الكارت).
  const needsCashbox = payment.method === 'cash';
  const balanceOk = !isCardBalance || (hasCard && cardBalance >= Math.min(paidNow || due, due));

  // نقص المبلغ لم يعد يمنع الخروج: الكاشير يُحصّل المبلغ الفعلي ويُقرّر مصير
  // الفرق (إسقاط أو دين). بدون قرار يبقى الزر معطّلاً حتى لا يضيع مال بالخطأ.
  const isShort = !isCredit && remaining > 0 && due > 0;
  const shortfall = payment.shortfall || null;
  const waiveNeedsReason = shortfall === 'waive' && (override.reason || '').trim().length < 3;

  const exact = isCredit || paidNow >= due || (paidNow === 0 && due === 0);
  const canSettle =
    (!needsCashbox || !!payment.cashboxId)
    && (exact || (!!shortfall && !waiveNeedsReason))
    && balanceOk
    && Number(totals?.membersCount || 0) > 0
    && !settleLoading
    && due >= 0;

  function fillDue() {
    if (paidNow === 0) setPayment({ paidAmount: due });
  }

  // الدفع من رصيد الكارت يساوي المستحق دائماً — لا فكة ولا زيادة.
  // نعيد المزامنة عند أي تغيّر في المستحق (مثلاً بعد تطبيق خصم)، وإلا بقي
  // المبلغ الأعلى وظهر «الباقي للزبون» وكأن هناك نقداً يُعاد.
  useEffect(() => {
    if (!isCardBalance) return;
    const target = cardBalance >= due ? due : 0;
    if (paidNow !== target) setPayment({ paidAmount: target });
  }, [isCardBalance, due, cardBalance, paidNow]); // eslint-disable-line react-hooks/exhaustive-deps

  const membersCount = Number(totals?.membersCount || 0);
  const totalMembers = Number(totals?.totalMembers || 0);

  return (
    <Card
      header="الدفع"
      sub={totalMembers > 1 ? `محاسبة ${membersCount} من ${totalMembers} أطفال` : 'تسوية الجلسة'}
    >
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-semibold text-ink-2 mb-1">طريقة الدفع</label>
          <div className="grid grid-cols-2 gap-1.5">
            {METHODS.map((m) => (
              <button
                key={m.key}
                type="button"
                className={`btn justify-center ${payment.method === m.key ? 'btn-primary' : ''}`}
                disabled={m.key === 'card_balance' && !hasCard}
                onClick={() => setPayment({ method: m.key, paidAmount: 0 })}
              >
                <span style={{ marginInlineEnd: 4 }}>{m.emoji}</span>
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {isCardBalance ? (
          <div
            className="rounded-[10px] p-2.5 text-[12.5px] space-y-1"
            style={{ background: balanceOk ? '#F0FDF4' : '#FEF2F2' }}
          >
            <div className="flex justify-between">
              <span className="text-ink-3">رصيد الكارت</span>
              <b>{formatIQD(cardBalance)}</b>
            </div>
            <div className="flex justify-between">
              <span className="text-ink-3">الرصيد بعد الخصم</span>
              <b style={{ color: balanceOk ? '#15803D' : '#B91C1C' }}>
                {formatIQD(Math.max(0, cardBalance - Math.min(paidNow || due, due)))}
              </b>
            </div>
            {!hasCard ? (
              <div className="text-red text-[11.5px]">لا يوجد كارت مرتبط بهذه الجلسة</div>
            ) : !balanceOk ? (
              <div className="text-red text-[11.5px]">رصيد الكارت غير كافٍ — اختر طريقة دفع أخرى</div>
            ) : (
              <div className="text-ink-3 text-[11px]">
                المبلغ يُخصم من رصيد الكارت ولا يدخل القاصة (دخل عند التعبئة).
              </div>
            )}
          </div>
        ) : (
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">
              القاصة {needsCashbox ? <span className="text-red">*</span> : <span className="text-ink-3 font-normal">(اختياري)</span>}
            </label>
            {shift ? <ShiftDrawerNote shift={shift} /> : (
              <>
            <select
              className="input"
              value={payment.cashboxId || ''}
              onChange={(e) => setPayment({ cashboxId: e.target.value })}
            >
              <option value="">— اختر القاصة —</option>
              {cashboxes.map((cb) => {
                const id = String(cb._id || cb.id);
                const isAssigned = assignedCashboxId && String(assignedCashboxId) === id;
                const typeLabel = CASHBOX_TYPE_LABELS[cb.type] || cb.type || '';
                return (
                  <option key={id} value={id}>
                    {cb.name}{typeLabel ? ` — ${typeLabel}` : ''}{isAssigned ? ' ★' : ''}
                  </option>
                );
              })}
            </select>
            {cashboxQuery.isLoading ? (
              <div className="text-[11px] text-ink-3 mt-1">جاري تحميل القاصات...</div>
            ) : !cashboxes.length ? (
              <div className="text-[11px] text-red mt-1">لا توجد قاصات نشطة — أضف قاصة من شاشة القاصات أولاً.</div>
            ) : assignedCashboxId && String(assignedCashboxId) === String(payment.cashboxId || '') ? (
              <div className="text-[11px] text-green mt-1">★ قاصتك المعيّنة</div>
            ) : null}
              </>
            )}
          </div>
        )}

        <div>
          <label className="block text-xs font-semibold text-ink-2 mb-1">المبلغ المدفوع</label>
          <input
            className="input"
            type="number"
            min="0"
            value={payment.paidAmount || ''}
            onFocus={fillDue}
            onChange={(e) => setPayment({ paidAmount: Number(e.target.value) || 0 })}
            placeholder={String(due || 0)}
            style={{ fontSize: 18, fontWeight: 700 }}
            disabled={isCredit || isCardBalance}
          />
          <div className="flex gap-1.5 mt-2">
            {QUICK_AMOUNTS.map((v) => (
              <button
                key={v}
                type="button"
                className="btn flex-1"
                style={{ padding: '6px 4px', fontSize: 11 }}
                onClick={() => setPayment({ paidAmount: paidNow + v })}
                disabled={isCredit || isCardBalance}
              >
                +{formatNumber(v)}
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-[10px] bg-paper-2 p-2.5 text-[12.5px] space-y-1">
          <div className="flex justify-between">
            <span className="text-ink-3">المتبقّي</span>
            <b style={{ color: due > 0 ? '#B91C1C' : '#15803D' }}>{formatIQD(due)}</b>
          </div>
          {isShort ? (
            <div className="flex justify-between text-red">
              <span>الفرق</span>
              <b>{formatIQD(remaining)}</b>
            </div>
          ) : null}
          {payment.method === 'cash' && change > 0 ? (
            <div className="flex justify-between text-green">
              <span>الباقي للزبون</span>
              <b>{formatIQD(change)}</b>
            </div>
          ) : null}
          {isCredit ? (
            <div className="flex justify-between text-orange-deep">
              <span>سيُسجَّل آجلاً</span>
              <b>{formatIQD(due)}</b>
            </div>
          ) : null}
        </div>

        {/* المبلغ المُحصَّل أقل من المستحق: الكاشير يُقرّر مصير الفرق صراحةً
            بدل أن يتعطّل الخروج. */}
        {isShort ? (
          <div className="rounded-[10px] p-2.5 space-y-2" style={{ background: '#FFF7ED' }}>
            <div className="text-[12px] font-bold text-orange-deep">
              المبلغ أقل من المستحق بـ {formatIQD(remaining)} — ماذا نفعل بالفرق؟
            </div>
            <div className="flex gap-1.5">
              <button
                type="button"
                className={`btn flex-1 justify-center ${shortfall === 'waive' ? 'btn-primary' : ''}`}
                style={{ fontSize: 12 }}
                onClick={() => setPayment({ shortfall: shortfall === 'waive' ? null : 'waive' })}
                disabled={!canOverride}
                title={canOverride ? '' : 'يتطلب صلاحية الخصم'}
              >
                إسقاط الفرق
              </button>
              <button
                type="button"
                className={`btn flex-1 justify-center ${shortfall === 'debt' ? 'btn-primary' : ''}`}
                style={{ fontSize: 12 }}
                onClick={() => setPayment({ shortfall: shortfall === 'debt' ? null : 'debt' })}
              >
                يبقى ديناً
              </button>
            </div>
            {shortfall === 'waive' ? (
              <>
                <input
                  className="input"
                  style={{ fontSize: 12 }}
                  value={override.reason || ''}
                  onChange={(e) => setOverride({ reason: e.target.value })}
                  placeholder="سبب إسقاط الفرق (مطلوب)"
                />
                <div className="text-[11px] text-ink-3">
                  يُسجَّل خصماً على رسوم اللعب مع اسمك وسبب الإسقاط للتدقيق.
                  فواتير الكافتيريا والألعاب لا يمكن إسقاطها.
                </div>
              </>
            ) : null}
            {shortfall === 'debt' ? (
              <div className="text-[11px] text-ink-3">
                يخرج الطفل الآن ويبقى {formatIQD(remaining)} مستحقاً على ولي الأمر.
              </div>
            ) : null}
            {!shortfall ? (
              <div className="text-[11px] text-red">اختر أحد الخيارين لإتمام الخروج.</div>
            ) : null}
          </div>
        ) : null}

        <button
          type="button"
          className="btn btn-success btn-xl w-full justify-center"
          disabled={!canSettle}
          onClick={onSettle}
        >
          <Icon name="check" size={18} />
          {settleLoading
            ? 'جاري التسوية...'
            : membersCount > 1
              ? `تسوية وخروج (${membersCount} أطفال)`
              : 'تسوية وخروج'}
        </button>
        {totalMembers > membersCount ? (
          <div className="text-[11px] text-ink-3 text-center leading-relaxed">
            سيبقى {totalMembers - membersCount} طفل داخل القاعة على نفس الكارت
          </div>
        ) : null}

        {canOverride ? (
          <button
            type="button"
            className="text-[12px] text-orange-deep hover:underline w-full text-center"
            onClick={onToggleOverride}
          >
            {override.active ? 'إلغاء الخصم' : 'إضافة خصم'}
          </button>
        ) : null}
      </div>
    </Card>
  );
}
